import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useCustomConfig } from '../composables/useCustomAxiosRequestConfig';
import { IChat, IChatMessage, IRecentChatGroup } from '../services/api/chat/chat';
import { CustomAxiosRequestConfig, http } from '../services/http';
import { __ } from './expressions';
import { useFilterStoreByName } from './filter';
import { FilterStoreName } from '../services/api/filter/types';

const NO_REALTIME_ANSWER_FALLBACK_MS = 30_000;
const NO_REALTIME_ANSWER_MAX_ATTEMPTS = 11;
const NO_ANSWER_TIMEOUT_MESSAGE_ID = 'ask-me-no-answer-timeout';

export const useAskMeStore = defineStore(
    'ask-me',
    () => {
        const chats = ref<IChat[]>([]);
        const messages = ref<IChatMessage[]>([]);
        const loading = ref(false);
        const filters_visible = ref(false);
        const currentChatId = ref<string | undefined>(undefined);

        const recentChats = computed(() => {
            let list: IRecentChatGroup[] = [];

            for (let chat of chats.value.sort((a, b) => {
                return new Date(a.latestMessageReceivedAt) > new Date(b.latestMessageReceivedAt)
                    ? -1
                    : 1;
            })) {
                let date = dayjs(chat.latestMessageReceivedAt);
                let group = date.format('YYYY-MM-DD');

                if (date.isSame(dayjs(), 'day')) {
                    group = __('today');
                } else if (date.isAfter(dayjs().subtract(2, 'days'), 'day')) {
                    group = __('yesterday');
                } else if (date.isAfter(dayjs().subtract(8, 'days'), 'day')) {
                    group = __('last-7-days');
                } else if (date.isAfter(dayjs().subtract(31, 'days'), 'day')) {
                    group = __('last-30-days');
                } else if (date.isSame(dayjs(), 'year')) {
                    group = date.format('MMMM');
                } else {
                    group = date.format('MMMM, YYYY');
                }

                let existingGroup = list.find((g) => g.group === group);

                if (!existingGroup) {
                    existingGroup = {
                        group,
                        chats: [],
                        latestMessageReceivedAt: chat.latestMessageReceivedAt,
                    };

                    list.push(existingGroup);
                }

                existingGroup.chats.push(chat);
            }

            return list;
        });

        const loadRecentChats = () => {
            http.get('chat/history').then((response) => {
                chats.value = response.data.chats as IChat[];
            });
        };

        const deleteChat = (chat_id: string) => {
            const index = chats.value.findIndex((c) => c.id === chat_id);

            if (index !== -1) {
                chats.value.splice(index, 1);
            }

            http.delete(
                `chat/{${chat_id}}`,
                useCustomConfig({
                    successMessage: __('ask-me-chat-deleted'),
                    silent: true,
                })
            ).then(() => {
                loadRecentChats();
            });
        };

        const updateChatName = (chat_id: string, name: string) => {
            http.patch(
                `chat/${chat_id}/name`,
                {
                    name,
                },
                useCustomConfig({
                    successMessage: __('ask-me-chat-name-updated'),
                    silent: true,
                })
            ).then(() => {
                loadRecentChats();
            });
        };

        const rateMessage = (
            chatId: string,
            messageId: string,
            rating: number,
            comment?: string
        ) => {
            return http.post(
                `chat/${chatId}/messages/${messageId}/rate`,
                {
                    rating,
                    comment,
                },
                useCustomConfig({
                    silent: true,
                })
            );
        };

        const clearMessages = () => {
            messages.value = [];
        };

        /**
         * Whether a chat's newest message is an unanswered user question.
         * Checks the last message only: prior history doesn't mean it's answered.
         * An empty list counts as awaiting too, the safer default when it's unclear.
         */
        const isAwaitingAnswer = (chatMessages: IChatMessage[]): boolean =>
            chatMessages.length === 0 ||
            chatMessages[chatMessages.length - 1].isUserMessage === true;

        /**
         * Fetches the chat and returns whether it's still awaiting an answer, so callers don't
         * have to re-read `loading` afterward: unrelated code can write it in between.
         */
        const loadChat = (
            chat_id: string,
            isStale?: () => boolean
        ): Promise<boolean | undefined> => {
            const config: CustomAxiosRequestConfig = {};

            if (messages.value.length > 0) {
                loading.value = true;
                config.silent = true;
            }

            return http.get(`chat/${chat_id}`, config).then((response) => {
                // A real-time answer can land while this request is in flight; don't clobber it.
                if (isStale?.()) {
                    return undefined;
                }

                const fetchedMessages = (response.data.messages as IChatMessage[]).map((message) =>
                    createMessageRows(message)
                );

                messages.value = fetchedMessages;

                const awaitingAnswer = isAwaitingAnswer(fetchedMessages);
                loading.value = awaitingAnswer;

                return awaitingAnswer;
            });
        };

        const clearUpdateMessages = () => {
            messages.value = messages.value.filter((message) => message.messageType !== 'Update');
        };

        const clearNoAnswerTimeoutMessage = () => {
            messages.value = messages.value.filter(
                (message) => !message.id?.startsWith(NO_ANSWER_TIMEOUT_MESSAGE_ID)
            );
        };

        let noRealtimeAnswerFallback: ReturnType<typeof window.setTimeout> | undefined;

        // Separate from `loading`, which also toggles off for interim 'Update' messages.
        let awaitingAnswerChatId: string | undefined;

        const receiveChatMessage = (message: IChatMessage) => {
            if (message.messageType === 'Update') {
                // A progress update proves the agent is still working: restart the give-up countdown.
                if (awaitingAnswerChatId) {
                    resumeWaitingForAnswer(awaitingAnswerChatId);
                }
            } else {
                clearTimeout(noRealtimeAnswerFallback);
                awaitingAnswerChatId = undefined;
            }
            clearUpdateMessages();
            clearNoAnswerTimeoutMessage();
            message = createMessageRows(message);
            messages.value.push(message);
            loading.value = false;
        };

        const createMessageRows = (message: IChatMessage): IChatMessage => {
            if (message.message) {
                message.messageRows = message.message
                    .split('\n')
                    .map((row) => row.trim())
                    .filter((row) => row.length > 0);
            }

            return message;
        };

        /**
         * Merges a fetched chat history with anything pushed since the fetch started, so
         * neither is discarded. The local echo (`'_'`) and any 'Update' ping are always
         * dropped: both are transient placeholders the fetch result already supersedes.
         */
        const mergeFetchedMessages = (fetched: IChatMessage[]): IChatMessage[] => {
            const fetchedIds = new Set(fetched.map((message) => message.id));
            const pushedSinceFetch = messages.value.filter(
                (message) =>
                    message.id !== '_' &&
                    message.messageType !== 'Update' &&
                    !fetchedIds.has(message.id)
            );

            return [...fetched.map((message) => createMessageRows(message)), ...pushedSinceFetch];
        };

        const giveUpOnRealtimeAnswer = () => {
            awaitingAnswerChatId = undefined;
            loading.value = false;
            addMessageToCurrentChat({
                id: `${NO_ANSWER_TIMEOUT_MESSAGE_ID}-${Date.now()}`,
                messageType: 'Error',
                isUserMessage: false,
                message: __('ask-me-no-answer-timeout'),
            });
        };

        // Bumped on every (re)arm so a chain superseded mid-flight (e.g. by an Update) can stop itself.
        let fallbackGeneration = 0;

        // currentChatId guards against acting on a chat the user has since navigated away from.
        const armNoRealtimeAnswerFallback = (chat_id: string, attempt: number) => {
            const generation = ++fallbackGeneration;

            const isStale = () =>
                generation !== fallbackGeneration ||
                awaitingAnswerChatId !== chat_id ||
                currentChatId.value !== chat_id;

            noRealtimeAnswerFallback = window.setTimeout(() => {
                if (isStale()) {
                    return;
                }

                console.warn(
                    'No real-time answer received in time, falling back to fetch:',
                    chat_id
                );

                // A failed fetch retries/gives up like an unanswered one would.
                loadChat(chat_id, isStale)
                    .catch((error) => {
                        console.error('Error loading chat:', error);
                        return true;
                    })
                    .then((stillAwaitingAnswer) => {
                        if (isStale()) {
                            return;
                        }

                        if (stillAwaitingAnswer === false) {
                            awaitingAnswerChatId = undefined;
                            return;
                        }

                        if (attempt >= NO_REALTIME_ANSWER_MAX_ATTEMPTS) {
                            giveUpOnRealtimeAnswer();
                        } else {
                            armNoRealtimeAnswerFallback(chat_id, attempt + 1);
                        }
                    });
            }, NO_REALTIME_ANSWER_FALLBACK_MS);
        };

        const resumeWaitingForAnswer = (chat_id: string) => {
            clearTimeout(noRealtimeAnswerFallback);
            awaitingAnswerChatId = chat_id;
            armNoRealtimeAnswerFallback(chat_id, 1);
        };

        /**
         * For a caller that finds the answer itself (e.g. a plain fetch) and needs to cancel a
         * fallback poll armed earlier for the same chat, instead of waiting for it to self-heal.
         */
        const stopWaitingForAnswer = (chat_id: string) => {
            if (awaitingAnswerChatId === chat_id) {
                clearTimeout(noRealtimeAnswerFallback);
                awaitingAnswerChatId = undefined;
            }
        };

        /**
         * Applies a freshly fetched chat history and re-derives whether it's still awaiting an
         * answer, keeping the fallback wait in sync with what's now actually displayed.
         */
        const applyFetchedMessages = (chat_id: string, fetched: IChatMessage[]) => {
            const merged = mergeFetchedMessages(fetched);

            if (isAwaitingAnswer(merged)) {
                loading.value = true;
                resumeWaitingForAnswer(chat_id);
            } else {
                loading.value = false;
                stopWaitingForAnswer(chat_id);
            }

            messages.value = merged;
        };

        const sendChatMessage = async (chat_id: string, content: string) => {
            loading.value = true;

            if (chat_id) {
                resumeWaitingForAnswer(chat_id);

                // No caller awaits or catches this: log and recover state here, don't rethrow.
                http.post(
                    `chat/${chat_id}/message`,
                    { content },
                    useCustomConfig({ silent: true })
                ).catch((error) => {
                    console.error('Error sending chat message:', error);

                    // Only clean up if this chat still owns the wait: the user may have
                    // switched to another chat that owns it now.
                    if (awaitingAnswerChatId === chat_id) {
                        loading.value = false;
                        clearTimeout(noRealtimeAnswerFallback);
                        awaitingAnswerChatId = undefined;
                    }
                });
            }
        };

        const createNewChat = (message: IChatMessage, filterStoreName: FilterStoreName) => {
            const { requestData } = useFilterStoreByName(filterStoreName);

            loading.value = true;

            return http
                .post('chat', {
                    message: message.message,
                    filter: requestData,
                })
                .then((response) => {
                    const newChatId = response.data.id as string | undefined;

                    if (newChatId) {
                        // currentChatId lands moments later, well within the 30s before this fires.
                        resumeWaitingForAnswer(newChatId);
                    } else {
                        console.error('Chat creation response had no id, fallback not armed');
                        loading.value = false;
                    }

                    return response;
                })
                .catch((error) => {
                    console.error('Error creating new chat:', error);

                    // This attempt never armed a wait of its own (no chat existed yet to arm one
                    // for), so only reset loading if no other chat has since claimed it.
                    if (!awaitingAnswerChatId) {
                        loading.value = false;
                    }
                    throw error;
                });
        };

        const addMessageToCurrentChat = (message: IChatMessage) => {
            if (!message.timestamp) {
                message.timestamp = new Date().toISOString();
            }

            if (!message.id) {
                message.id = '_';
            }

            message = createMessageRows(message);

            messages.value.push(message);
        };

        const updateCurrentChatId = (id: string | undefined) => {
            currentChatId.value = id;
        };

        return {
            chats,
            messages,
            loading,
            recentChats,
            filters_visible,
            loadRecentChats,
            loadChat,
            applyFetchedMessages,
            sendChatMessage,
            receiveChatMessage,
            clearMessages,
            deleteChat,
            updateChatName,
            rateMessage,
            createNewChat,
            addMessageToCurrentChat,
            createMessageRows,
            currentChatId,
            updateCurrentChatId,
        };
    },
    {
        persist: {
            storage: sessionStorage,
            pick: ['filters_visible'],
        },
    }
);
